import { urlForImage } from '@/sanity/lib/utils'

export type SanityImage = {
  asset?: { _ref: string; _type: 'reference' } | any
  hotspot?: any
  crop?: any
}

/**
 * Fetches SVG content from a Sanity image asset and processes it for inline rendering
 * @param image - Sanity image object with asset reference
 * @param companyName - Optional name for error logging
 * @returns SVG content string with fill attributes replaced with currentColor, or null on error
 */
export async function fetchSvgContent(
  image: SanityImage | null | undefined,
  companyName?: string,
): Promise<string | null> {
  if (!image) return null

  const imageUrl = urlForImage(image)?.url()
  if (!imageUrl) return null

  try {
    const response = await fetch(imageUrl)
    if (!response.ok) {
      console.error(
        `Failed to fetch SVG${companyName ? ` for ${companyName}` : ''}: ${response.status}`,
      )
      return null
    }
    let svgContent = await response.text()

    // Replace fill attributes with currentColor to allow CSS color styling
    svgContent = svgContent.replace(/fill="[^"]*"/g, 'fill="currentColor"')

    // Add viewBox if missing to ensure proper scaling
    if (!svgContent.includes('viewBox')) {
      const widthMatch = svgContent.match(/width="(\d+(?:\.\d+)?)"/)
      const heightMatch = svgContent.match(/height="(\d+(?:\.\d+)?)"/)

      if (widthMatch && heightMatch) {
        const width = widthMatch[1]
        const height = heightMatch[1]
        svgContent = svgContent.replace(/<svg/, `<svg viewBox="0 0 ${width} ${height}"`)
      }
    }

    return svgContent
  } catch (error) {
    console.error(`Failed to fetch SVG${companyName ? ` for ${companyName}` : ''}:`, error)
    return null
  }
}
